/**
 * Cobertura do Catálogo WRICEF: o quanto da base já foi analisado.
 *
 * O catálogo (`project_wricef_catalog`) é por projeto e cumulativo — tanto o
 * "recorte" do form-run quanto a "Varredura da base" gravam nele. Mas a tela
 * não dizia se os N objetos visíveis são a base inteira ou só um pedaço. Este
 * helper deriva o estado de cobertura pra UI comunicar isso e oferecer a ponte
 * recorte → base inteira.
 *
 * Denominador = `extracted_programs` (abap_source_index), o universo que a
 * extração da varredura descobriu. Sem extração (só form-run), não há base de
 * referência → estado "slice" (recorte).
 */
export type CoverageMode = "empty" | "slice" | "partial" | "covered";

export interface CoverageInput {
  /** Linhas no catálogo do projeto. **Não** é o mesmo que analisados. */
  catalogTotal: number;
  /** Objetos extraídos pra o índice pela varredura (universo analisável). */
  extractedPrograms: number;
  /**
   * Catalogados **sem** classificação de clean core (`_UNKNOWN` do
   * `by_clean_core_level`). A linha nasce na catalogação; o nível só aparece
   * quando a análise roda, então catalogado sem nível é objeto que entrou na
   * tabela e não passou pela análise.
   *
   * Opcional para a chamada antiga não mudar de estado. Quando ausente, o
   * helper volta a assumir catalogado == analisado, que é o que ele fazia.
   */
  unclassified?: number;
  /**
   * O universo de ORIGEM: quantos objetos com fonte existem no sistema, sem
   * filtro de pacote (#829).
   *
   * É o denominador que responde a pergunta do cliente — "quanto do meu código
   * vocês olharam?" — em vez de "quanto do recorte que eu mesmo escolhi olhar eu
   * olhei", que é o que o `extractedPrograms` responde.
   *
   * Ausente quando o projeto nunca extraiu nada. Nesse caso **não há
   * percentual**: ausência de denominador é ausência de resposta, e é o mesmo
   * vocabulário que a #752 usa para vitalidade não medida.
   */
  sourceTotal?: number;
}

export interface Coverage {
  mode: CoverageMode;
  /** Objetos que de fato passaram pela análise. */
  analyzed: number;
  /** Linhas no catálogo. Difere de `analyzed` quando falta classificação. */
  catalogued: number;
  extracted: number;
  /** 0–100, inteiro. Só significativo em 'partial'/'covered'. */
  pct: number;
  /** true quando ainda há base a cobrir (slice ou partial) — dispara o CTA. */
  isPartial: boolean;
  /**
   * A varredura já alcançou toda a base extraída? Separa os dois `partial` que
   * pedem ações opostas: varrer mais pacotes, ou classificar o que já está
   * catalogado. Varrer não resolve o segundo (#829).
   */
  baseFullyCatalogued: boolean;
  /**
   * Linhas catalogadas sem nível de clean core — a lacuna que varrer mais NÃO
   * resolve (#829).
   *
   * Existe à parte do `baseFullyCatalogued` porque aquele compara o catálogo com
   * o universo de origem, e num projeto de recorte ele é sempre falso: 252 de
   * 7599. As linhas sem classificação são reais nos dois casos, e amarrá-las
   * àquele booleano fazia o consultor receber "há mais base para varrer" quando
   * o que falta é reprocessar o que já está catalogado.
   */
  unclassified: number;
}

export function wricefCoverage({
  catalogTotal,
  extractedPrograms,
  unclassified = 0,
  sourceTotal,
}: CoverageInput): Coverage {
  // #829: o denominador é o universo de ORIGEM quando ele existe.
  //
  // O `extractedPrograms` é do último job, e o `analyzed` é cumulativo. Medido
  // no projeto `ee5cbccc`: 148 analisados de 252 linhas contra os 91 do último
  // job. Com 148 >= 91 o veredito virava `covered` por comparar grandezas de
  // escopos diferentes — e se o último job varrer um pacote pequeno, o
  // denominador encolhe e a cobertura "fecha" sem ter fechado.
  //
  // David escolheu esta opção em 14/09: "é a única de fato sincera, as outras
  // seriam baseadas no que escolhemos te mostrar".
  const denominator = sourceTotal && sourceTotal > 0 ? sourceTotal : extractedPrograms;
  // Catalogado não é analisado (#829). A linha entra na tabela na catalogação;
  // o nível de clean core só aparece quando a análise roda. Medido em
  // 12/09/2026: 104 dos 252 objetos de um projeto estavam sem nível, e a tela
  // dizia "everything extracted has been analyzed".
  const analyzed = Math.max(catalogTotal - unclassified, 0);

  if (catalogTotal <= 0) {
    return {
      mode: "empty",
      analyzed: 0,
      catalogued: 0,
      extracted: extractedPrograms,
      pct: 0,
      isPartial: false,
      baseFullyCatalogued: false,
      unclassified: 0,
    };
  }
  if (denominator <= 0) {
    // Catálogo existe mas a varredura nunca extraiu — é um recorte avulso.
    return {
      mode: "slice",
      analyzed,
      catalogued: catalogTotal,
      extracted: 0,
      pct: 0,
      isPartial: true,
      baseFullyCatalogued: false,
      unclassified,
    };
  }
  // `covered` compara ANALISADOS com extraídos: catalogar sem classificar não
  // fecha cobertura, e era exatamente essa a afirmação errada.
  if (analyzed >= denominator) {
    return {
      mode: "covered",
      analyzed,
      catalogued: catalogTotal,
      extracted: denominator,
      pct: 100,
      isPartial: false,
      baseFullyCatalogued: true,
      unclassified,
    };
  }
  return {
    mode: "partial",
    analyzed,
    catalogued: catalogTotal,
    extracted: denominator,
    pct: Math.round((analyzed / denominator) * 100),
    isPartial: true,
    baseFullyCatalogued: catalogTotal >= denominator,
    unclassified,
  };
}
